import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BotidService } from '@shared-angular/services/ragai/botid.service';
import { UsertokenService } from '@shared-angular/services/usertoken.service';
import QueryString from 'qs';
import { firstValueFrom } from 'rxjs';
import { EventManagerService } from '../../event-manager/event-manager.service';
import { AiImageFile } from './openaiImage.service';

/** Erzeugt und bearbeitet Bilder per fal.ai (`RAGAI.AIIMAGE_FALAI`). */
/**
 * Erzeugt und bearbeitet Bilder per fal.ai (`RAGAI.AIIMAGE_FALAI`).
 * @remarks
 * `opts.image`/`opts.images` verwandeln eine reine Text-zu-Bild-Generierung in eine Bild-Bearbeitung
 * (Referenzbild(er) werden mitgeschickt) - ob das zugrunde liegende fal.ai-Modell (nano-banana/nano-banana-edit)
 * Generierung oder Edit durchführt, entscheidet der Server anhand dieser Parameter. Strukturell nahezu identisch
 * zu {@link OpenaiImageService} und {@link StableDiffusionImageService} - alle drei sind austauschbare
 * Bild-Provider mit ähnlicher API, unterscheiden sich vor allem in Qualität/Kosten/Fähigkeiten des Modells.
 */
@Injectable({
    providedIn: 'root'
})
export class FalaiImageService {
    constructor(
        private eventManager: EventManagerService,
        private http: HttpClient,
        private botIdService: BotidService,
        private usertokenService: UsertokenService
    ) { }

    /**
     * Generiert oder bearbeitet ein Bild per fal.ai-Modell.
     * @param prompt Der Text-Prompt für die Bildgenerierung.
     * @param opts Zusätzliche Optionen.
     * @param opts.image Referenzbild-URL für die Bildbearbeitung.
     * @param opts.images Mehrere Referenzbilder für die Bildbearbeitung.
     * @param opts.aspectRatio Gewünschtes Seitenverhältnis des Ergebnisbildes.
     * @returns Die erzeugte Bilddatei.
     */
    public async create(prompt: string, opts: { image?: string, images?: string[], aspectRatio?: string } = {}) {
        this.eventManager._trigger_RAGAI_EVENT("RAGAI.AIIMAGE_FALAI.CREATE.start", { prompt, opts });

        const query = QueryString.stringify({
            prompt,
            ...opts,
            ...(opts.images && { images: JSON.stringify(opts.images) })
        });
        const result = await firstValueFrom(this.http.get<AiImageFile>("/api/ai/image/create/falai/" + this.botIdService.getBotId() + "?" + query));
        this.eventManager._trigger_RAGAI_EVENT("RAGAI.AIIMAGE_FALAI.CREATE.done", { prompt, opts, result });

        return result;
    }

    /**
     * Erstellt eine ladbare URL für ein zuvor erzeugtes Bild.
     * @param fileId ID der Bilddatei.
     * @returns Die URL zum Laden des Bildes.
     */
    public getImageUrl(fileId: string) {
        return "/api/ai/image/load/" + this.botIdService.getBotId() + "/" + fileId + "?Ut=" + this.usertokenService.getUserToken();
    }
}
